import * as Dialog from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { cn } from '@/lib/utils';
import { useIsMobile, useIsTablet } from '@/lib/hooks/useMediaQuery';
import XBoldIcon from '@/icons/XBold.svg?react';
import type { UserGuideModalContent } from './types';
import type { ScreenReaderInstruction } from '@/components/sections/HomeUserGuideModal/types';

/**
 * Renders visually hidden, keyboard-focusable instruction steps for screen readers.
 * Each step is a span with sr-only class and tabIndex={0} so screen reader users
 * can Tab through the instructions that are visually conveyed by the screenshot image.
 */
function ScreenReaderInstructions({
  instructions,
}: {
  instructions: ScreenReaderInstruction[];
}) {
  if (instructions.length === 0) return null;

  return (
    <div data-testid="sr-instructions" role="group" aria-label="User guide instructions">
      {instructions.map((step, index) => (
        <span
          key={index}
          className="sr-only"
          tabIndex={0}
          data-testid={`sr-instruction-${index + 1}`}
        >
          {`${step.heading.replace(/:$/, '')}. ${step.description}`}
        </span>
      ))}
    </div>
  );
}

/**
 * Props interface for ServiceDirectoryUserGuideModal component
 */
export interface ServiceDirectoryUserGuideModalProps {
  /** Modal content */
  content?: UserGuideModalContent;
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * ServiceDirectoryUserGuideModal - Visual guide modal for the Service Directory
 *
 * Displays a responsive screenshot-based user guide with visually hidden
 * screen reader instructions. Follows the Home/Events user guide modal pattern.
 *
 * Responsive behavior:
 * - Desktop (>= 1200px): 1212px modal with desktop guide image, close button overlays image
 * - Tablet (800px - 1199px): 716px scrollable modal with tablet guide image
 * - Mobile (< 800px): 396px scrollable modal with mobile guide image
 */
export function ServiceDirectoryUserGuideModal({
  content,
  isOpen,
  onClose,
  className,
}: ServiceDirectoryUserGuideModalProps) {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  if (!content) {
    return null;
  }

  // Select appropriate image based on viewport
  const imageSrc = isMobile
    ? content.images.mobile
    : isTablet
      ? content.images.tablet
      : content.images.desktop;

  const instructions = content.screenReaderInstructions ?? [];

  // Mobile layout: fixed width container with scrollable image area
  if (isMobile) {
    return (
      <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <Dialog.Portal>
          <Dialog.Overlay
            className="fixed inset-0 bg-black/50 z-[130]"
            data-testid="user-guide-modal-overlay"
            onClick={onClose}
          />
          <Dialog.Content
            className={cn(
              'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
              'z-[131]',
              'focus:outline-none',
              'flex flex-col',
              'bg-white rounded-[4px]',
              className
            )}
            style={{
              width: '396px',
              maxWidth: 'calc(100vw - 48px)',
              maxHeight: 'calc(100vh - 48px)',
              boxShadow: '0px 4px 4px 0px rgba(187, 187, 187, 0.25)',
            }}
            aria-describedby={undefined}
            data-testid="user-guide-modal-content"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            {/* Visually hidden title for accessibility */}
            <VisuallyHidden.Root asChild>
              <Dialog.Title>{content.title}</Dialog.Title>
            </VisuallyHidden.Root>

            {/* Exit Button - fixed at top, not scrolling */}
            <div className="flex justify-end p-4 shrink-0">
              <Dialog.Close
                className="w-10 h-10 flex items-center justify-center hover:opacity-70 transition-opacity cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
                aria-label={content.closeButtonLabel}
                data-testid="user-guide-modal-close"
              >
                <XBoldIcon className="w-5 h-5 text-black" aria-hidden="true" />
              </Dialog.Close>
            </div>

            {/* Screen reader instructions (visually hidden, keyboard-focusable) */}
            {instructions.length > 0 && (
              <ScreenReaderInstructions instructions={instructions} />
            )}

            {/* Scrollable image area */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden">
              <img
                src={imageSrc}
                alt=""
                className="block w-full"
                style={{ marginTop: '-24px' }}
                data-testid="user-guide-screenshot"
              />
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  // Tablet layout: fixed dimensions with scrollable content
  if (isTablet) {
    return (
      <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <Dialog.Portal>
          <Dialog.Overlay
            className="fixed inset-0 bg-black/50 z-[130]"
            data-testid="user-guide-modal-overlay"
            onClick={onClose}
          />
          <Dialog.Content
            className={cn(
              'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
              'z-[131]',
              'focus:outline-none',
              'flex flex-col',
              'bg-white rounded-[4px]',
              className
            )}
            style={{
              width: '716px',
              maxWidth: 'calc(100vw - 48px)',
              maxHeight: 'calc(100vh - 48px)',
              boxShadow: '0px 4px 4px 0px rgba(187, 187, 187, 0.25)',
            }}
            aria-describedby={undefined}
            data-testid="user-guide-modal-content"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            {/* Visually hidden title for accessibility */}
            <VisuallyHidden.Root asChild>
              <Dialog.Title>{content.title}</Dialog.Title>
            </VisuallyHidden.Root>

            {/* Exit Button - fixed at top, not scrolling */}
            <div className="flex justify-end p-4 shrink-0">
              <Dialog.Close
                className="w-10 h-10 flex items-center justify-center hover:opacity-70 transition-opacity cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
                aria-label={content.closeButtonLabel}
                data-testid="user-guide-modal-close"
              >
                <XBoldIcon className="w-5 h-5 text-black" aria-hidden="true" />
              </Dialog.Close>
            </div>

            {/* Screen reader instructions (visually hidden, keyboard-focusable) */}
            {instructions.length > 0 && (
              <ScreenReaderInstructions instructions={instructions} />
            )}

            {/* Scrollable image area */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden">
              <img
                src={imageSrc}
                alt=""
                className="block w-full"
                style={{ marginTop: '-24px' }}
                data-testid="user-guide-screenshot"
              />
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  // Desktop layout: image drives height, close button overlays on image
  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 bg-black/50 z-[130]"
          data-testid="user-guide-modal-overlay"
          onClick={onClose}
        />
        <Dialog.Content
          className={cn(
            'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
            'z-[131]',
            'focus:outline-none',
            className
          )}
          style={{
            maxWidth: 'calc(100vw - 48px)',
            maxHeight: 'calc(100vh - 48px)',
          }}
          aria-describedby={undefined}
          data-testid="user-guide-modal-content"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* Visually hidden title for accessibility */}
          <VisuallyHidden.Root asChild>
            <Dialog.Title>{content.title}</Dialog.Title>
          </VisuallyHidden.Root>

          {/* Screen reader instructions (visually hidden, keyboard-focusable) */}
          {instructions.length > 0 && (
            <ScreenReaderInstructions instructions={instructions} />
          )}

          {/* Screenshot */}
          <img
            src={imageSrc}
            alt=""
            className="block max-w-[90vw] max-h-[90vh] object-contain"
            data-testid="user-guide-screenshot"
          />

          {/* Exit Button - overlayed on top right */}
          <Dialog.Close
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center hover:opacity-70 transition-opacity cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            aria-label={content.closeButtonLabel}
            data-testid="user-guide-modal-close"
          >
            <XBoldIcon className="w-5 h-5 text-white" aria-hidden="true" />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

ServiceDirectoryUserGuideModal.displayName = 'ServiceDirectoryUserGuideModal';
